import React from "react";
import { graphql, useLazyLoadQuery, useQueryLoader } from "react-relay";
import { useParams, useNavigate } from "react-router-dom";
import { InversionSolutionQuery } from "./__generated__/InversionSolutionQuery.graphql";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import InversionSolutionDetailTab from "./InversionSolutionDetailTab";
import { Tab, Tabs, Typography, styled } from "@mui/material";
import TabPanel from "../../components/common/TabPanel";
import DiagnosticReportTab from "./DiagnosticReportTab";
import RuptureSetReportTab from "./RuptureSetReportTab";
import InversionSolutionMfdTab from "./InversionSolutionMfdTab";
import CentredCircularProgress from "../../components/common/CentredCircularProgress";
import PageContainer from "../../components/common/PageContainer";
import TabContainer from "../../components/common/TabContainer";
import FavouriteControls from "../../components/common/FavouriteControls";
import InversionSolutionSourcesTab from "./InversionSolutionSourcesTab";
import PredecessorsTab, { hasPredecessors, predecessorsTabQuery } from "../../components/common/PredecessorsTab";
import { PredecessorsTabQuery } from "../../components/common/__generated__/PredecessorsTabQuery.graphql";
import { useMfdTableMeta } from "../../components/common/MfdChart";

export type InversionSolutionParams = {
  id: string;
  tab: string;
};

const StyledTab = styled(Tab)(({ theme }) => ({
  maxWidth: "5vw",
  borderRight: `2px solid ${theme.palette.divider}`,
}));

const NotFoundContainer = styled("div")(() => ({
  flexGrow: 1,
  padding: 20,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}));

const InversionSolution: React.FC = () => {
  const { id, tab } = useParams<InversionSolutionParams>();
  const data = useLazyLoadQuery<InversionSolutionQuery>(inversionSolutionQuery, { id: id || "" });
  const navigate = useNavigate();
  const mfdTableMeta = useMfdTableMeta(data);
  const [predQueryRef, loadPredQuery] = useQueryLoader<PredecessorsTabQuery>(predecessorsTabQuery);

  React.useEffect(() => {
    if (!id) {
      return;
    }
    switch (tab) {
      case "PredecessorsTab":
        loadPredQuery({ id }, { fetchPolicy: "network-only" });
        break;
    }
  }, [id, loadPredQuery, tab]);

  const ruptureSetId = data?.node?.meta?.filter((kv) => kv?.k == "rupture_set_file_id")[0]?.v;
  const sourceSolutions = data?.node?.source_solutions;

  const renderTab = () => {
    switch (tab) {
      default:
        return <TabPanel>{data && <InversionSolutionDetailTab data={data} />}</TabPanel>;
      case "MfdTab":
        return (
          <TabPanel>
            <React.Suspense fallback={<CentredCircularProgress />}>
              {mfdTableMeta && <InversionSolutionMfdTab mfdTableMeta={mfdTableMeta} nodeMeta={data?.node?.meta} />}
            </React.Suspense>
          </TabPanel>
        );
      case "SolutionReportTab":
        return <TabPanel>{id && data && <DiagnosticReportTab id={id} />}</TabPanel>;
      case "RuptureSetReportTab":
        return (
          <TabPanel>{ruptureSetId && data && <RuptureSetReportTab fileId={ruptureSetId} metaAsString="" />}</TabPanel>
        );
      case "SourceSolutionsTab":
        return (
          <TabPanel>{sourceSolutions && <InversionSolutionSourcesTab sourceSolutions={sourceSolutions} />}</TabPanel>
        );
      case "PredecessorsTab":
        return (
          <TabPanel>
            <React.Suspense fallback={<CentredCircularProgress />}>
              {predQueryRef && <PredecessorsTab queryRef={predQueryRef} />}
            </React.Suspense>
          </TabPanel>
        );
    }
  };

  if (!data?.node?.file_name) {
    return (
      <NotFoundContainer>
        <Typography variant="h5" gutterBottom>
          Inversion Solution ID Not Found
        </Typography>
      </NotFoundContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5" gutterBottom>
        {data?.node?.__typename}: {id}&nbsp;
      </Typography>
      <FavouriteControls id={id || ""} />
      <TabContainer maxWidth="xl">
        <Tabs
          orientation="vertical"
          value={tab ?? "DetailsTab"}
          onChange={(_e, val) => {
            navigate(`/InversionSolution/${id}/${val}`);
          }}
        >
          <StyledTab label="Details" id="DetailsTab" value={"DetailsTab"} />
          <StyledTab label="MFD Plot" id="MfdTab" value={"MfdTab"} />
          <StyledTab label="Solution Diags" id="SolutionReportTab" value="SolutionReportTab" />
          {sourceSolutions && <StyledTab label="Sources" id="SourceSolutionsTab" value="SourceSolutionsTab" />}
          {ruptureSetId && <StyledTab label="Rupture Diags" id="RuptureSetReportTab" value="RuptureSetReportTab" />}
          {hasPredecessors(data.node?.__typename) && (
            <StyledTab label="Prede-cesssors" id="PredecessorsTab" value="PredecessorsTab" />
          )}
        </Tabs>
        {renderTab()}
      </TabContainer>
    </PageContainer>
  );
};

const InversionSolutionPage: React.FC = () => {
  return <React.Suspense fallback={<SimpleBackdrop />}>{<InversionSolution />}</React.Suspense>;
};

export default InversionSolutionPage;

const inversionSolutionQuery = graphql`
  query InversionSolutionQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Node {
        id
      }
      ... on FileInterface {
        file_name
        file_url
        file_size
        md5_digest
        meta {
          k
          v
        }
        relations {
          total_count
        }
      }
      ... on InversionSolutionInterface {
        mfd_table_id
        mfd_table {
          id
        }
        hazard_table_id
        created
        produced_by {
          ... on Node {
            id
          }
        }
        tables {
          table_id
          table_type
          created
        }
      }
      ... on TimeDependentInversionSolution {
        source_solution {
          ... on Node {
            id
          }
          ... on InversionSolution {
            created
          }
        }
      }
      ... on ScaledInversionSolution {
        source_solution {
          ... on Node {
            id
          }
          ... on InversionSolution {
            created
          }
          ... on InversionSolutionInterface {
            created
            produced_by {
              __typename
              ... on Node {
                id
              }
            }
          }
        }
      }
      ... on AggregateInversionSolution {
        common_rupture_set {
          id
          file_url
        }
        aggregation_fn
        source_solutions {
          ... on Node {
            id
          }
          ... on FileInterface {
            file_name
            file_url
          }
        }
      }
      ... on InversionSolutionNrml {
        source_solution {
          ... on AggregateInversionSolution {
            id
          }
          ... on InversionSolution {
            id
          }
          ... on ScaledInversionSolution {
            id
          }
          ... on TimeDependentInversionSolution {
            id
          }
        }
      }
    }
  }
`;
